import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message, Modal } from 'antd';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';

import { adminPaths } from '../../../constants/paths';
import { workScheduleQueryKeys } from '../api/queryKeys';
import { useChildWorkSchedulesQuery } from '../api/useChildWorkSchedulesQuery';
import { useParentWorkSchedulesQuery } from '../api/useParentWorkSchedulesQuery';
import {
  createChildWorkSchedule,
  createParentWorkSchedule,
  deleteChildWorkSchedule,
  deleteParentWorkSchedule,
  updateChildWorkSchedule,
  updateParentWorkSchedule,
} from '../api/workScheduleApi';
import {
  ensureSuccessResponse,
  getBackendErrorResponse,
  isConflictResponse,
} from '../api/workScheduleApiResponse';
import { workScheduleByPeriodFormModes } from '../components/CreateNewWorkScheduleByPeriod/CreateNewWorkScheduleByPeriod.constants';
import { scheduleGroupIdSearchParam } from '../model/workSchedule.constants';
import { findSavedWorkSchedule } from '../model/findSavedWorkSchedule';
import {
  getSchedulesForGroup,
  getWorkersWithIntervals,
  getWorkersWithoutIntervals,
  hasScheduleIntervals,
} from '../model/workScheduleDrafts';
import {
  confirmSaveWithoutWorkerIntervals,
  confirmUnsavedChanges,
} from '../model/workScheduleConfirmations';
import { useWorkScheduleDrafts } from './useWorkScheduleDrafts';
import { useWorkScheduleNavigationBlocker } from './useWorkScheduleNavigationBlocker';

const noop = () => {};

const showWorkScheduleError = (error, fallbackMessage) => {
  if (error?.response) {
    message.error(fallbackMessage || 'Произошла ошибка');
    return;
  }

  message.error(error?.message || fallbackMessage);
};

const getWorkerShortName = (worker = {}) => {
  const nameParts = [worker.surName, worker.firstName, worker.patrName].filter(
    Boolean
  );

  if (nameParts.length) {
    const [surName, firstName = '', patrName = ''] = nameParts;
    const initials = [firstName, patrName]
      .filter(Boolean)
      .map((name) => `${name[0]}.`)
      .join('');

    return [surName, initials].filter(Boolean).join(' ');
  }

  const [surName = '', firstName = '', patrName = ''] = (worker.fullName || '')
    .trim()
    .split(/\s+/);
  const initials = [firstName, patrName]
    .filter(Boolean)
    .map((name) => `${name[0]}.`)
    .join('');

  return [surName, initials].filter(Boolean).join(' ') || '—';
};

export const useWorkScheduleController = ({ companyId }) => {
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const skipNextNavigationBlockRef = useRef(false);
  const savedDraftScheduleIdsRef = useRef(new Set());
  const workScheduleByPeriodSourceRef = useRef(null);
  const parentSchedulesQuery = useParentWorkSchedulesQuery(
    { companyId },
    {
      onError: (error) => {
        showWorkScheduleError(error, 'Произошла ошибка при получении графиков');
      },
    }
  );
  const scheduleGroups = parentSchedulesQuery.data || [];
  const searchParams = new URLSearchParams(location.search);
  const selectedScheduleGroupId = searchParams.get(scheduleGroupIdSearchParam);
  const parentSchedulesQueryKey =
    workScheduleQueryKeys.parentSchedules(companyId);

  const selectedScheduleGroupBase = useMemo(
    () =>
      scheduleGroups.find(
        (scheduleGroup) => String(scheduleGroup.id) === selectedScheduleGroupId
      ) ?? null,
    [scheduleGroups, selectedScheduleGroupId]
  );
  const childSchedulesQuery = useChildWorkSchedulesQuery(
    { workScheduleId: selectedScheduleGroupBase?.id },
    {
      onError: (error) => {
        showWorkScheduleError(
          error,
          'Произошла ошибка при получении графиков по периоду'
        );
      },
    }
  );
  const selectedScheduleGroup = useMemo(() => {
    if (!selectedScheduleGroupBase) {
      return null;
    }

    return {
      ...selectedScheduleGroupBase,
      children: childSchedulesQuery.data || [],
    };
  }, [childSchedulesQuery.data, selectedScheduleGroupBase]);
  const childSchedulesQueryKey = workScheduleQueryKeys.childSchedules(
    selectedScheduleGroupBase?.id
  );

  const [editingScheduleGroup, setEditingScheduleGroup] = useState(null);
  const [isCreateNewWorkScheduleVisible, setIsCreateNewWorkScheduleVisible] =
    useState(false);
  const [isCreateNewWorkScheduleDirty, setIsCreateNewWorkScheduleDirty] =
    useState(false);
  const [
    isCreateNewWorkScheduleByPeriodVisible,
    setIsCreateNewWorkScheduleByPeriodVisible,
  ] = useState(false);
  const [
    isCreateNewWorkScheduleByPeriodDirty,
    setIsCreateNewWorkScheduleByPeriodDirty,
  ] = useState(false);
  const [workScheduleByPeriodMode, setWorkScheduleByPeriodMode] = useState(
    workScheduleByPeriodFormModes.create
  );
  const drafts = useWorkScheduleDrafts({ selectedScheduleGroup });
  const {
    assignDraftIntervals,
    cachedChildSchedules,
    createDraftWorkScheduleByPeriod,
    hasUnsavedDraft,
    removeCachedChildSchedule,
    selectedSchedule,
    selectedSchedules,
    setSelectedSchedule,
    setWorkScheduleByPeriodSource,
    workScheduleByPeriodSource,
  } = drafts;

  const isChildScheduleList = Boolean(selectedScheduleGroup);

  const invalidateParentSchedules = useCallback(
    () => queryClient.invalidateQueries(parentSchedulesQueryKey),
    [parentSchedulesQueryKey, queryClient]
  );
  const invalidateChildSchedules = useCallback(
    () => queryClient.invalidateQueries(childSchedulesQueryKey),
    [childSchedulesQueryKey, queryClient]
  );

  useEffect(() => {
    workScheduleByPeriodSourceRef.current = workScheduleByPeriodSource;
  }, [workScheduleByPeriodSource]);

  const runConfirmedNavigation = useCallback((onOk) => {
    skipNextNavigationBlockRef.current = true;
    onOk();
    skipNextNavigationBlockRef.current = false;
  }, []);

  const hideCreateNewWorkSchedule = useCallback(() => {
    setIsCreateNewWorkScheduleVisible(false);
    setIsCreateNewWorkScheduleDirty(false);
    setEditingScheduleGroup(null);
  }, []);

  const hideCreateNewWorkScheduleByPeriod = useCallback(() => {
    const source = workScheduleByPeriodSourceRef.current;

    if (source?.isDraft) {
      removeCachedChildSchedule(source.id);
    }

    setIsCreateNewWorkScheduleByPeriodVisible(false);
    setIsCreateNewWorkScheduleByPeriodDirty(false);
    setWorkScheduleByPeriodMode(workScheduleByPeriodFormModes.create);
    setWorkScheduleByPeriodSource(null);
  }, [removeCachedChildSchedule, setWorkScheduleByPeriodSource]);

  const saveParentScheduleMutation = useMutation(
    async (values) => {
      const isEditing = values.id !== undefined && values.id !== null;
      const response = isEditing
        ? await updateParentWorkSchedule({
            workScheduleId: values.id,
            workScheduleName: values.name,
          })
        : await createParentWorkSchedule({ ...values, companyId });

      return ensureSuccessResponse(
        response,
        isEditing ? 'Не удалось изменить график' : 'Не удалось создать график'
      );
    },
    {
      onSuccess: async (response, values) => {
        setIsCreateNewWorkScheduleDirty(false);
        hideCreateNewWorkSchedule();
        await invalidateParentSchedules();

        const createdScheduleId = response?.id;

        if (
          (values.id === undefined || values.id === null) &&
          createdScheduleId !== undefined &&
          createdScheduleId !== null
        ) {
          history.push({
            pathname: adminPaths.workSchedule(companyId),
            search: `?${scheduleGroupIdSearchParam}=${encodeURIComponent(
              String(createdScheduleId)
            )}`,
          });
        }
      },
      onError: (error, values) => {
        showWorkScheduleError(
          error,
          values?.id !== undefined && values?.id !== null
            ? 'Произошла ошибка при изменении графика'
            : 'Произошла ошибка при создании графика'
        );
      },
    }
  );

  const deleteParentScheduleMutation = useMutation(
    async (values) => {
      const response = await deleteParentWorkSchedule(values);

      return ensureSuccessResponse(response, 'Не удалось удалить график');
    },
    {
      onSuccess: async () => {
        await invalidateParentSchedules();
      },
      onError: (error) => {
        showWorkScheduleError(error, 'Произошла ошибка при удалении графика');
      },
    }
  );

  const saveChildScheduleMutation = useMutation(
    async (values) => {
      const previousIds = (
        queryClient.getQueryData(childSchedulesQueryKey) || []
      ).map((schedule) => schedule.id);
      const isEditing =
        values._draftMode === workScheduleByPeriodFormModes.edit;
      const response = isEditing
        ? await updateChildWorkSchedule(values)
        : await createChildWorkSchedule(values);

      ensureSuccessResponse(
        response,
        isEditing
          ? 'Не удалось изменить график по периоду'
          : 'Не удалось создать график по периоду'
      );
      return { response, previousIds };
    },
    {
      onSuccess: async ({ response, previousIds }, values) => {
        setIsCreateNewWorkScheduleByPeriodDirty(false);

        await invalidateChildSchedules();
        const savedSchedule = findSavedWorkSchedule({
          response,
          values,
          previousIds,
          schedules: queryClient.getQueryData(childSchedulesQueryKey) || [],
        });
        if (savedSchedule) {
          setSelectedSchedule(savedSchedule);
        }

        if (values.isDraft) {
          savedDraftScheduleIdsRef.current.add(values.id);
          removeCachedChildSchedule(values.id);
          setWorkScheduleByPeriodSource(null);
        } else {
          hideCreateNewWorkScheduleByPeriod();
        }

        if (!savedSchedule) {
          message.error(
            'График сохранён, но не найден в обновлённом списке. Выберите его в списке графиков.'
          );
        }
      },
      onError: (error, values) => {
        const backendResponse = getBackendErrorResponse(error);
        const isCreating =
          values?._draftMode !== workScheduleByPeriodFormModes.edit;

        if (isCreating && isConflictResponse(backendResponse)) {
          Modal.warning({
            title: 'Невозможно создать график по периоду',
            content:
              backendResponse?.message ||
              'За выбранный период уже существует действующий график.',
          });
          return;
        }

        if (isCreating && error?.response) {
          message.error('При создании графика произошла ошибка');
          return;
        }

        showWorkScheduleError(
          error,
          values?._draftMode === workScheduleByPeriodFormModes.edit
            ? 'Произошла ошибка при изменении графика по периоду'
            : 'Произошла ошибка при создании графика по периоду'
        );
      },
    }
  );

  const deleteChildScheduleMutation = useMutation(
    async (values) => {
      const response = await deleteChildWorkSchedule(values);

      return ensureSuccessResponse(
        response,
        'Не удалось удалить график по периоду'
      );
    },
    {
      onSuccess: async (_response, values) => {
        setSelectedSchedule((prevSchedule) =>
          String(prevSchedule?.id) === String(values.id) ? null : prevSchedule
        );
        await invalidateChildSchedules();
      },
      onError: (error) => {
        showWorkScheduleError(
          error,
          'Произошла ошибка при удалении графика по периоду'
        );
      },
    }
  );

  const hideDirtyWorkScheduleForms = useCallback(() => {
    hideCreateNewWorkSchedule();
    hideCreateNewWorkScheduleByPeriod();
  }, [hideCreateNewWorkSchedule, hideCreateNewWorkScheduleByPeriod]);

  const confirmHideCreateNewWorkSchedule = useCallback(
    (onOk = noop) => {
      if (!isCreateNewWorkScheduleDirty) {
        hideCreateNewWorkSchedule();
        onOk();
        return;
      }

      confirmUnsavedChanges({
        title: 'Вы уверены, что хотите скрыть форму?',
        onOk: () => {
          hideCreateNewWorkSchedule();
          runConfirmedNavigation(onOk);
        },
      });
    },
    [
      hideCreateNewWorkSchedule,
      isCreateNewWorkScheduleDirty,
      runConfirmedNavigation,
    ]
  );

  const confirmDiscardDraftSchedule = useCallback(
    (onOk = noop) => {
      if (!selectedSchedule?.isDraft) {
        onOk();
        return;
      }

      if (savedDraftScheduleIdsRef.current.has(selectedSchedule.id)) {
        savedDraftScheduleIdsRef.current.delete(selectedSchedule.id);
        removeCachedChildSchedule(selectedSchedule.id);
        setWorkScheduleByPeriodSource(null);
        onOk();
        return;
      }

      confirmUnsavedChanges({
        title: 'Вы уверены, что хотите покинуть график?',
        onOk: () => {
          removeCachedChildSchedule(selectedSchedule.id);
          setWorkScheduleByPeriodSource(null);
          runConfirmedNavigation(onOk);
        },
      });
    },
    [
      removeCachedChildSchedule,
      runConfirmedNavigation,
      selectedSchedule,
      setWorkScheduleByPeriodSource,
    ]
  );

  const confirmHideCreateNewWorkScheduleByPeriod = useCallback(
    (onOk = noop) => {
      if (!isCreateNewWorkScheduleByPeriodDirty) {
        hideCreateNewWorkScheduleByPeriod();
        onOk();
        return;
      }

      confirmUnsavedChanges({
        title: 'Вы уверены, что хотите скрыть форму?',
        onOk: () => {
          hideCreateNewWorkScheduleByPeriod();
          runConfirmedNavigation(onOk);
        },
      });
    },
    [
      hideCreateNewWorkScheduleByPeriod,
      isCreateNewWorkScheduleByPeriodDirty,
      runConfirmedNavigation,
    ]
  );

  const showWorkScheduleByPeriodForm = useCallback(
    (mode, schedule = null) => {
      setWorkScheduleByPeriodMode(mode);
      setWorkScheduleByPeriodSource(schedule);
      setIsCreateNewWorkScheduleByPeriodVisible(true);
      setIsCreateNewWorkScheduleByPeriodDirty(false);
    },
    [setWorkScheduleByPeriodSource]
  );

  const handleSelectScheduleGroup = useCallback(
    (scheduleGroup) => {
      const selectScheduleGroup = () => {
        setSelectedSchedule(null);
        history.push({
          pathname: adminPaths.workSchedule(companyId),
          search: `?${scheduleGroupIdSearchParam}=${encodeURIComponent(
            String(scheduleGroup.id)
          )}`,
        });
      };

      if (isCreateNewWorkScheduleVisible) {
        confirmHideCreateNewWorkSchedule(selectScheduleGroup);
        return;
      }

      if (selectedSchedule?.isDraft) {
        confirmDiscardDraftSchedule(selectScheduleGroup);
        return;
      }

      selectScheduleGroup();
    },
    [
      companyId,
      confirmDiscardDraftSchedule,
      confirmHideCreateNewWorkSchedule,
      history,
      isCreateNewWorkScheduleVisible,
      selectedSchedule,
      setSelectedSchedule,
    ]
  );

  const handleCreateButtonClick = useCallback(() => {
    const showCreateByPeriodForm = () => {
      showWorkScheduleByPeriodForm(workScheduleByPeriodFormModes.create);
    };

    if (isChildScheduleList) {
      if (selectedSchedule?.isDraft) {
        confirmDiscardDraftSchedule(showCreateByPeriodForm);
        return;
      }

      showCreateByPeriodForm();
      return;
    }

    if (isCreateNewWorkScheduleVisible) {
      confirmHideCreateNewWorkSchedule();
      return;
    }

    setEditingScheduleGroup(null);
    setIsCreateNewWorkScheduleVisible(true);
  }, [
    confirmDiscardDraftSchedule,
    confirmHideCreateNewWorkSchedule,
    isChildScheduleList,
    isCreateNewWorkScheduleVisible,
    selectedSchedule,
    showWorkScheduleByPeriodForm,
  ]);

  const handleSubmitNewWorkSchedule = useCallback(
    (values) => saveParentScheduleMutation.mutate(values),
    [saveParentScheduleMutation]
  );

  const handleEditScheduleGroup = useCallback(
    (scheduleGroup) => {
      const showEditForm = () => {
        setEditingScheduleGroup(scheduleGroup);
        setIsCreateNewWorkScheduleVisible(true);
        setIsCreateNewWorkScheduleDirty(false);
      };

      if (isCreateNewWorkScheduleVisible && isCreateNewWorkScheduleDirty) {
        confirmHideCreateNewWorkSchedule(showEditForm);
        return;
      }

      showEditForm();
    },
    [
      confirmHideCreateNewWorkSchedule,
      isCreateNewWorkScheduleDirty,
      isCreateNewWorkScheduleVisible,
    ]
  );

  const handleDeleteScheduleGroup = useCallback(
    (scheduleGroup) => {
      Modal.confirm({
        title: 'Удалить график?',
        content: scheduleGroup.departmentName,
        okText: 'Удалить',
        okType: 'danger',
        cancelText: 'Отмена',
        onOk: () =>
          deleteParentScheduleMutation.mutateAsync({
            companyId,
            id: scheduleGroup.id,
          }),
      });
    },
    [companyId, deleteParentScheduleMutation]
  );

  const handleCreateDraftWorkScheduleByPeriod = useCallback(
    (values) => {
      const draftSchedule = createDraftWorkScheduleByPeriod(values);

      setIsCreateNewWorkScheduleByPeriodVisible(false);
      setIsCreateNewWorkScheduleByPeriodDirty(false);

      return draftSchedule;
    },
    [createDraftWorkScheduleByPeriod]
  );

  const handleSaveDraftWorkScheduleByPeriod = useCallback(() => {
    if (!selectedSchedule?.isDraft) {
      return;
    }

    if (!getWorkersWithIntervals(selectedSchedule).length) {
      Modal.warning({
        title: 'Заполните обязательные данные',
        content:
          'Необходимо задать интервал времени работы минимум одного исполнителя',
      });
      return;
    }

    const workersWithoutIntervals =
      getWorkersWithoutIntervals(selectedSchedule);
    const saveDraft = () => {
      saveChildScheduleMutation.mutate(selectedSchedule);
      setIsCreateNewWorkScheduleByPeriodDirty(false);
    };

    if (!workersWithoutIntervals.length) {
      saveDraft();
      return;
    }

    confirmSaveWithoutWorkerIntervals({
      workerNames: workersWithoutIntervals.map(getWorkerShortName),
      onOk: saveDraft,
    });
  }, [saveChildScheduleMutation, selectedSchedule]);

  const handleCancelDraftWorkScheduleByPeriod = useCallback(() => {
    confirmDiscardDraftSchedule();
  }, [confirmDiscardDraftSchedule]);

  const handleBackToDraftWorkScheduleSettings = useCallback(() => {
    if (!selectedSchedule?.isDraft) {
      return;
    }

    showWorkScheduleByPeriodForm(
      selectedSchedule._draftMode || workScheduleByPeriodFormModes.create,
      selectedSchedule
    );
  }, [selectedSchedule, showWorkScheduleByPeriodForm]);

  const handleEditChildSchedule = useCallback(
    (schedule) => {
      showWorkScheduleByPeriodForm(
        workScheduleByPeriodFormModes.edit,
        schedule
      );
    },
    [showWorkScheduleByPeriodForm]
  );

  const handleDuplicateChildSchedule = useCallback(
    (schedule) => {
      showWorkScheduleByPeriodForm(
        workScheduleByPeriodFormModes.duplicate,
        schedule
      );
    },
    [showWorkScheduleByPeriodForm]
  );

  const handleDeleteChildSchedule = useCallback(
    (schedule) => {
      Modal.confirm({
        title: 'Удалить график по периоду?',
        content: schedule.departmentName,
        okText: 'Удалить',
        okType: 'danger',
        cancelText: 'Отмена',
        onOk: () =>
          deleteChildScheduleMutation.mutateAsync({
            id: schedule.periodId ?? schedule.id,
          }),
      });
    },
    [deleteChildScheduleMutation]
  );

  const handleSelectChildSchedule = useCallback(
    (schedule) => {
      if (selectedSchedule?.isDraft && selectedSchedule.id !== schedule?.id) {
        confirmDiscardDraftSchedule(() => setSelectedSchedule(schedule));
        return;
      }

      setSelectedSchedule(schedule);
    },
    [confirmDiscardDraftSchedule, selectedSchedule, setSelectedSchedule]
  );

  const handleBackToScheduleGroups = useCallback(() => {
    const backToScheduleGroups = () => {
      setSelectedSchedule(null);
      history.push(adminPaths.workSchedule(companyId));
    };

    if (selectedSchedule?.isDraft) {
      confirmDiscardDraftSchedule(backToScheduleGroups);
      return;
    }

    if (isCreateNewWorkScheduleByPeriodVisible) {
      confirmHideCreateNewWorkScheduleByPeriod(backToScheduleGroups);
      return;
    }

    backToScheduleGroups();
  }, [
    companyId,
    confirmDiscardDraftSchedule,
    confirmHideCreateNewWorkScheduleByPeriod,
    history,
    isCreateNewWorkScheduleByPeriodVisible,
    selectedSchedule,
    setSelectedSchedule,
  ]);

  const schedules = useMemo(
    () =>
      getSchedulesForGroup({
        cachedChildSchedules: cachedChildSchedules,
        isChildScheduleList,
        scheduleGroups,
        selectedScheduleGroup,
      }),
    [
      cachedChildSchedules,
      isChildScheduleList,
      scheduleGroups,
      selectedScheduleGroup,
    ]
  );
  const showBackButtonAsTitle = Boolean(selectedScheduleGroup?.departmentName);
  const shouldBlockNavigation =
    (isCreateNewWorkScheduleVisible && isCreateNewWorkScheduleDirty) ||
    (isCreateNewWorkScheduleByPeriodVisible &&
      isCreateNewWorkScheduleByPeriodDirty) ||
    hasUnsavedDraft;

  useWorkScheduleNavigationBlocker({
    hideDirtyWorkScheduleForms,
    shouldBlockNavigation,
    skipNextNavigationBlockRef,
  });

  useEffect(() => {
    setSelectedSchedule(null);
    hideCreateNewWorkScheduleByPeriod();
  }, [
    hideCreateNewWorkScheduleByPeriod,
    selectedScheduleGroupId,
    setSelectedSchedule,
  ]);

  return {
    calendar: {
      companyId,
      canSaveIntervals: hasScheduleIntervals(selectedSchedule),
      enableIntervalAssignment: Boolean(selectedSchedule?.isDraft),
      onAssignIntervals: assignDraftIntervals,
      onBackIntervals: handleBackToDraftWorkScheduleSettings,
      onCancelIntervals: handleCancelDraftWorkScheduleByPeriod,
      onSaveIntervals: handleSaveDraftWorkScheduleByPeriod,
      selectedSchedules: selectedSchedules,
    },
    createByPeriodForm: {
      companyId,
      isVisible: isCreateNewWorkScheduleByPeriodVisible,
      key: `${workScheduleByPeriodMode}-${
        workScheduleByPeriodSource?.id ?? 'new'
      }`,
      mode: workScheduleByPeriodMode,
      onCancel: () => confirmHideCreateNewWorkScheduleByPeriod(),
      onDirtyChange: setIsCreateNewWorkScheduleByPeriodDirty,
      onDraftCreate: handleCreateDraftWorkScheduleByPeriod,
      parentScheduleId: selectedScheduleGroup?.id,
      parentScheduleName: selectedScheduleGroup?.departmentName,
      schedule: workScheduleByPeriodSource,
      isSubmitting: saveChildScheduleMutation.isLoading,
    },
    createGroupForm: {
      isVisible: isCreateNewWorkScheduleVisible && !isChildScheduleList,
      key: editingScheduleGroup?.id ?? 'create',
      isSubmitting: saveParentScheduleMutation.isLoading,
      onCancel: () => confirmHideCreateNewWorkSchedule(),
      onDirtyChange: setIsCreateNewWorkScheduleDirty,
      onSubmit: handleSubmitNewWorkSchedule,
      schedule: editingScheduleGroup,
    },
    scheduleList: {
      createButtonIcon:
        isCreateNewWorkScheduleVisible && !isChildScheduleList
          ? 'close'
          : 'plus',
      createButtonText: isChildScheduleList
        ? 'Новый график по периоду'
        : 'Новый график',
      onBack: handleBackToScheduleGroups,
      onCreateButtonClick: handleCreateButtonClick,
      onDeleteSchedule: isChildScheduleList
        ? handleDeleteChildSchedule
        : handleDeleteScheduleGroup,
      onDuplicateSchedule: isChildScheduleList
        ? handleDuplicateChildSchedule
        : undefined,
      onEditSchedule: isChildScheduleList
        ? handleEditChildSchedule
        : handleEditScheduleGroup,
      onSelectSchedule: isChildScheduleList
        ? handleSelectChildSchedule
        : handleSelectScheduleGroup,
      isLoading: isChildScheduleList
        ? childSchedulesQuery.isLoading || childSchedulesQuery.isFetching
        : parentSchedulesQuery.isLoading || parentSchedulesQuery.isFetching,
      schedules,
      selectedSchedule: isChildScheduleList ? selectedSchedule : null,
      showBackButton: showBackButtonAsTitle,
      showPeriodFilter: isChildScheduleList,
      showScheduleDetails: isChildScheduleList,
      title: selectedScheduleGroup?.departmentName || 'Графики',
    },
    selectedSchedule: selectedSchedule,
  };
};
